import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import { Label } from "components/ui/label";
import { Trash2 } from "lucide-react";

function UserTable({ users, onRoleChange, onDeleteUser, onPermissionChange }) {
  const roles = ["admin", "editor", "viewer", "pending"];

  return (
    <div>
      {/* MOBILE CARDS VIEW */}
      <div className="block lg:hidden space-y-4">
        {users.map((user) => (
          <div
            key={user.id}
            className="p-4 border rounded-xl shadow-sm bg-white dark:bg-card flex flex-col gap-4"
          >
            <div className="flex justify-between items-start gap-2">
              <div className="overflow-hidden">
                <div className="font-semibold text-base truncate">
                  {user.name || "Nome não disponível"}
                </div>
                <div className="text-sm text-muted-foreground truncate">
                  {user.email}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive hover:bg-destructive/10 shrink-0 h-8 w-8"
                onClick={() => onDeleteUser(user.id, user.name)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">
                Função Global
              </Label>
              <Select
                value={user.role}
                onValueChange={(newRole) => onRoleChange(user.id, newRole)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Definir função..." />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role} value={role}>
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="pt-3 border-t">
              <Label className="text-xs font-semibold text-muted-foreground mb-3 block uppercase tracking-wider">
                Permissões de Acesso
              </Label>
              <div className="grid grid-cols-2 gap-y-3 gap-x-2">
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <Checkbox
                    checked={user.permissions?.canViewExcursions ?? true}
                    onCheckedChange={(isChecked) =>
                      onPermissionChange(
                        user.id,
                        "canViewExcursions",
                        isChecked,
                      )
                    }
                  />
                  Excursões
                </label>
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <Checkbox
                    checked={user.permissions?.canViewChales ?? true}
                    onCheckedChange={(isChecked) =>
                      onPermissionChange(user.id, "canViewChales", isChecked)
                    }
                  />
                  Chalés
                </label>
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <Checkbox
                    checked={!!user.permissions?.canViewDashboard}
                    onCheckedChange={(isChecked) =>
                      onPermissionChange(
                        user.id,
                        "canViewDashboard",
                        isChecked,
                      )
                    }
                  />
                  Dashboard
                </label>
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <Checkbox
                    checked={!!user.permissions?.canViewEstoque}
                    onCheckedChange={(isChecked) =>
                      onPermissionChange(user.id, "canViewEstoque", isChecked)
                    }
                  />
                  Estoque
                </label>
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <Checkbox
                    checked={!!user.permissions?.canViewGuias}
                    onCheckedChange={(isChecked) =>
                      onPermissionChange(user.id, "canViewGuias", isChecked)
                    }
                  />
                  Guias
                </label>
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <Checkbox
                    checked={!!user.permissions?.canViewFinanceiro}
                    onCheckedChange={(isChecked) =>
                      onPermissionChange(user.id, "canViewFinanceiro", isChecked)
                    }
                  />
                  Histórico Financeiro
                </label>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* DESKTOP TABLE VIEW */}
      <div className="hidden lg:block rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Usuário</TableHead>
              <TableHead>Email</TableHead>
              <TableHead className="w-[180px]">Função Global</TableHead>
              <TableHead className="text-center">Acesso Excursões</TableHead>
              <TableHead className="text-center">Acesso Chalés</TableHead>
              <TableHead className="text-center">Acesso Dashboard</TableHead>
              <TableHead className="text-center">Acesso Estoque</TableHead>
              <TableHead className="text-center">Acesso Guias</TableHead>
              <TableHead className="text-center">Acesso Financeiro</TableHead>
              <TableHead className="text-right w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="font-medium">
                  {user.name || "Nome não disponível"}
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Select
                    value={user.role}
                    onValueChange={(newRole) => onRoleChange(user.id, newRole)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Definir função..." />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((role) => (
                        <SelectItem key={role} value={role}>
                          {role.charAt(0).toUpperCase() + role.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>

                <TableCell className="text-center">
                  <Checkbox
                    checked={user.permissions?.canViewExcursions ?? true}
                    onCheckedChange={(isChecked) =>
                      onPermissionChange(
                        user.id,
                        "canViewExcursions",
                        isChecked,
                      )
                    }
                  />
                </TableCell>

                <TableCell className="text-center">
                  <Checkbox
                    checked={user.permissions?.canViewChales ?? true}
                    onCheckedChange={(isChecked) =>
                      onPermissionChange(user.id, "canViewChales", isChecked)
                    }
                  />
                </TableCell>

                <TableCell className="text-center">
                  <Checkbox
                    checked={!!user.permissions?.canViewDashboard}
                    onCheckedChange={(isChecked) =>
                      onPermissionChange(
                        user.id,
                        "canViewDashboard",
                        isChecked,
                      )
                    }
                  />
                </TableCell>

                <TableCell className="text-center">
                  <Checkbox
                    checked={!!user.permissions?.canViewEstoque}
                    onCheckedChange={(isChecked) =>
                      onPermissionChange(user.id, "canViewEstoque", isChecked)
                    }
                  />
                </TableCell>

                <TableCell className="text-center">
                  <Checkbox
                    checked={!!user.permissions?.canViewGuias}
                    onCheckedChange={(isChecked) =>
                      onPermissionChange(user.id, "canViewGuias", isChecked)
                    }
                  />
                </TableCell>

                <TableCell className="text-center">
                  <Checkbox
                    checked={!!user.permissions?.canViewFinanceiro}
                    onCheckedChange={(isChecked) =>
                      onPermissionChange(user.id, "canViewFinanceiro", isChecked)
                    }
                  />
                </TableCell>

                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    onClick={() => onDeleteUser(user.id, user.name)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default UserTable;
